'use client';

import { cropVideoApi, updateClipCropStatusApi } from '@repo/sdk';
import { useCallback, useState } from 'react';

interface IUseCropActionReturn {
  isCropping: boolean;
  error: string | null;
  cropClip: (
    clipId: number,
    vaultPath: string,
    startMs: number,
    endMs: number,
    filename: string,
  ) => Promise<boolean>;
}

export function useCropAction(): IUseCropActionReturn {
  const [isCropping, setIsCropping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cropClip = useCallback(
    async (
      clipId: number,
      vaultPath: string,
      startMs: number,
      endMs: number,
      filename: string,
    ): Promise<boolean> => {
      setIsCropping(true);
      setError(null);
      try {
        await updateClipCropStatusApi({
          clipId,
          body: { cropStatus: 'cropping', errorMessage: null },
        });

        const result = await cropVideoApi({
          vaultPath,
          startMs,
          endMs,
          outputFilename: filename,
        });

        await updateClipCropStatusApi({
          clipId,
          body: {
            cropStatus: 'cropped',
            baseLocalPath: result.localPath,
            durationMs: result.durationMs,
            fileSizeKb: result.fileSizeKb,
          },
        });
        return true;
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Crop failed';
        setError(msg);
        await updateClipCropStatusApi({
          clipId,
          body: { cropStatus: 'failed', errorMessage: msg },
        }).catch(() => {});
        return false;
      } finally {
        setIsCropping(false);
      }
    },
    [],
  );

  return { isCropping, error, cropClip };
}
